/** Whole minutes since an ISO instant. Negative clock skew reads as 0. */
export function minutesSince(iso: string | null | undefined, now = Date.now()): number {
  if (!iso) return 0;
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.round((now - parsed) / 60_000));
}

/** "just now" / "14m" / "3h 20m" / "2d 4h" -- the old dashboard's `duration()`. */
export function duration(minutes: number): string {
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${days}d ${rest}h` : `${days}d`;
}

/**
 * A duration read as a moment in the past.
 *
 * "1h 11m" is an interval, and a column of them is read as one: a heartbeat
 * every 1h 11m rather than a heartbeat 1h 11m ago. The unit belongs on the
 * number. `duration` already answers "just now" under a minute, and "just now
 * ago" is not a phrase, so that one case is passed through as it is -- which is
 * the whole reason this is a function and not a template literal at four call
 * sites.
 */
export function since(minutes: number): string {
  return minutes < 1 ? 'just now' : `${duration(minutes)} ago`;
}

/**
 * The coarsest useful age, for the live rail: one unit, never two. A column of
 * "36m / 1h / 4h / 1d" is monotonic at a glance where "1h 41m / 4h 6m" is two
 * numbers per row and wraps at eleven pixels of column.
 */
export function ago(minutes: number): string {
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

/**
 * The stamp on a journal line, 24-hour.
 *
 * Deliberately not the locale's 12-hour form: a journal is read as a column, and
 * "23:52" is five tabular characters where "11:52 PM" is eight and wraps a 44px
 * cell onto two lines. Time alone while the item is from today; anything older
 * carries its date, because these lists are newest-first and a column of bare
 * times spanning several days reads as broken sorting. The year appears only
 * once the item is not from this one.
 */
export function clockTime(iso: string, now = Date.now()): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '--:--';
  const time = parsed.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const today = new Date(now);
  if (
    parsed.getFullYear() === today.getFullYear() &&
    parsed.getMonth() === today.getMonth() &&
    parsed.getDate() === today.getDate()
  ) {
    return time;
  }
  const date = parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(parsed.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  });
  return `${date}, ${time}`;
}

/**
 * A deadline as a person reads one. An invite good for another 28 hours rendered
 * as "expires 23:13" is indistinguishable from one that died this morning, which
 * is the whole question an admin looks at that column to answer, so the distance
 * is what gets printed and the exact stamp goes in the tooltip.
 */
export function relativeDeadline(iso: string, now = Date.now()): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return 'expiry unknown';
  const minutes = Math.round(Math.abs(parsed - now) / 60_000);
  const span = minutes < 1 ? 'less than a minute' : duration(minutes);
  return parsed >= now ? `expires in ${span}` : `expired ${span} ago`;
}

export function percent(fraction: number): string {
  return `${Math.round(Math.max(0, Math.min(1, fraction)) * 100)}%`;
}

/**
 * Up to two initials for an avatar, from whatever string names a person: a
 * full name splits on whitespace, and a bare email or username with no space
 * in it falls through to its first character, the same shape `admin.tsx`'s
 * people list already accepts for a contributor with no display name on file.
 * `·` is the fallback for an empty string so an avatar never renders blank.
 */
export function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((word) => word[0] ?? '')
      .join('')
      .slice(0, 2)
      .toUpperCase() || '·'
  );
}

/**
 * Mirrors `@dyspatch/core`'s `nanoUsdToUsd` / `formatUsd` (`cost.ts`, issue
 * #18) rather than importing them: a real import would pull `@dyspatch/core`'s
 * runtime values into this bundle, and with them `node:crypto` by way of
 * `ids.ts` (see the mirrored lists this package already carries). `format.test.ts`
 * pins this against the same cases `cost.test.ts` checks in core, so the two
 * cannot drift apart.
 *
 * Nanodollars in, because that is what the API sends - never a float dollar
 * amount (`TaskAttemptUsage.costNanoUsd`). Null reads as "unknown", not "$0":
 * an attempt the agent reported nothing for is not free.
 */
export function formatUsd(nanoUsd: number | null | undefined): string {
  if (nanoUsd === null || nanoUsd === undefined) return 'unknown';
  const usd = nanoUsd / 1_000_000_000;
  return `$${usd > 0 && usd < 0.01 ? usd.toFixed(4) : usd.toFixed(2)}`;
}

/** "unknown" / "842" / "12.3k" / "1.4M" - a token count too, never a false zero. */
export function formatTokens(tokens: number | null | undefined): string {
  if (tokens === null || tokens === undefined) return 'unknown';
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}k`;
  return `${tokens}`;
}

/**
 * A file size as a person reads one: "512 B", "84 KB", "1.4 MB".
 *
 * Decimal units rather than binary, because the numbers this is printed beside
 * are the hub's own caps (10 MB a file, 25 MB a job, `describeSize` in
 * `routes/admin.ts`) and those are round decimal numbers. "9.5 MiB" next to a
 * 10 MB limit reads as a different quantity than the one being compared.
 *
 * One decimal place under 10 units and none above it, so a column of sizes
 * stays about as wide as it is informative. Anything that is not a real,
 * non-negative number reads as "unknown" rather than as "0 B": a size nobody
 * reported is not an empty file.
 */
const BYTE_UNITS = ['KB', 'MB', 'GB'] as const;

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return 'unknown';
  if (!Number.isFinite(bytes) || bytes < 0) return 'unknown';
  if (bytes < 1_000) return `${Math.round(bytes)} B`;
  let value = bytes / 1_000;
  let unit = 0;
  while (value >= 1_000 && unit < BYTE_UNITS.length - 1) {
    value /= 1_000;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${BYTE_UNITS[unit]}`;
}

/**
 * The word a person reads for a device's `headroomWindow` (issue #22): the
 * console used to say "five-hour window" everywhere, no matter which of the
 * vendor's two windows actually produced the number. `null` covers a device
 * that has never reported one - it gets no word rather than a guessed one, the
 * same reasoning `HeadroomBar` already applies to an unmeasured headroom.
 */
/**
 * Where a headroom figure came from, as a person reads it.
 *
 * `oauth_cached` is the vendor's own reading being served while its endpoint
 * refuses to answer again (issue #121). It is still the best number on the page
 * - the transcript estimate it replaced was wrong by 5.8x and took a working
 * device off the roster - but it is only honest if it says how old it is.
 */
export function headroomSourceLabel(
  source: string | null | undefined,
  measuredAt?: string | null,
  now = Date.now(),
): string {
  if (source !== 'oauth_cached') return source ?? '';
  if (!measuredAt) return 'oauth (cached)';
  const minutes = minutesSince(measuredAt, now);
  return minutes < 1 ? 'oauth (cached)' : `oauth (cached, ${duration(minutes)} old)`;
}

export function headroomWindowLabel(window: string | null | undefined): string {
  if (window === 'seven_day') return 'weekly';
  if (window === 'five_hour') return 'five-hour';
  return '';
}

/**
 * How long a wait has to be before the console says something about it. One
 * rule, read the same way from every page.
 *
 * Three answers used to live on one screen: `Age` turned amber at four hours,
 * the review queue called a PR stale at eight, and "Oldest wait" never coloured
 * at all, so the same gate looked urgent in the list and unremarkable in the
 * strip directly above it. The hub owns the interval -- `staleAfterMinutes`
 * rides along with the review metrics -- and late is half of it, which is where
 * the four hours came from in the first place.
 */
export const STALE_MINUTES = 8 * 60;

export interface WaitThresholds {
  /** Amber: long enough that somebody should look. */
  late: number;
  /** Red: the hub itself calls this stale. */
  stale: number;
}

/** The thresholds, preferring the hub's number over the one compiled in here. */
export function waitThresholds(staleAfterMinutes?: number | null): WaitThresholds {
  const stale = staleAfterMinutes && staleAfterMinutes > 0 ? staleAfterMinutes : STALE_MINUTES;
  return { late: Math.round(stale / 2), stale };
}

export type WaitLevel = 'fresh' | 'late' | 'stale';

export function waitLevel(
  minutes: number,
  thresholds: WaitThresholds = waitThresholds(),
): WaitLevel {
  if (minutes >= thresholds.stale) return 'stale';
  if (minutes >= thresholds.late) return 'late';
  return 'fresh';
}
